import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useViewSort, mapSortForEntity } from '../context/ViewSortContext'
import { useReleases, useArtists, useLabels, useWantlist } from '../hooks'
import { getSellerAllIds, getSellerProgress, SellerProgress } from '../api/seller'
import { getSavedSeller, addSavedSeller, refreshSavedSeller, getSellerListings, pauseSeller, resumeSeller, SellerListingInfo } from '../api/sellers'
import { track } from '../utils/track'
import { getFolders, getFolderMasterFilter } from '../api/folders'
import { useToast } from '../context/ToastContext'
import { useDiscogsConnection } from '../context/DiscogsContext'
import { SellerTabContext } from '../context/SellerTabContext'
import { Release, ReleaseFilters as ReleaseFiltersType } from '../types/release'
import { ArtistFilters, ArtistSortField } from '../types/artist'
import { LabelFilters, LabelSortField } from '../types/label'
import { cn } from '../utils'
import { LoadingState, Pagination, ReleaseToolbar, Button, IconButton, SearchField, Select } from '../components/common'
import { ReleaseFilters } from '../components/releases/ReleaseFilters'
import { ReleaseGrid } from '../components/releases/ReleaseGrid'
import { ReleaseCrateView } from '../components/releases/ReleaseCrateView'
import { ReleaseListView } from '../components/releases/ReleaseListView'
import { ViewSortControls } from '../components/common/ViewSortControls'
import { ArtistGrid } from '../components/artists/ArtistGrid'
import { LabelGrid } from '../components/labels/LabelGrid'
import styles from './PageStyles.module.css'
import releaseStyles from '../components/releases/Releases.module.css'
import artistStyles from '../components/artists/Artists.module.css'
import labelStyles from '../components/labels/Labels.module.css'

// Returns a percentile rank in [0, 1]: fraction of values that are <= `value`.
function percentileRank(value: number, sorted: number[]): number {
  if (sorted.length === 0) return 0.5
  let rank = 0
  for (const v of sorted) { if (v <= value) rank++ }
  return rank / sorted.length
}

// Read a release field by sort column name.
function getQualityValue(r: Release, sortBy: string): number {
  switch (sortBy) {
    case 'elevated_score':  return r.elevated_score ?? 0
    case 'match_score':     return r.match_score ?? 0
    case 'bayesian_rating': return r.bayesian_rating ?? 0
    case 'age_adjusted_rating': return r.age_adjusted_rating ?? 0
    case 'rating_average':  return r.rating_average ?? 0
    case 'want':            return r.want ?? 0
    case 'have':            return r.have ?? 0
    case 'want_have_ratio': return r.want_have_ratio ?? 0
    case 'median':          return r.median ?? 0
    default:                return r.elevated_score ?? 0
  }
}

// Slider: t=0 cheapest, t=0.5 best deals, t=1 quality (matches server sort).
function computeDealScores(
  releases: Release[],
  listingMap: Map<number, SellerListingInfo>,
  t: number,
  sortBy: string,
  sortOrder: string,
): Map<number, number> {
  if (t >= 1) {
    const m = new Map<number, number>()
    releases.forEach((r, i) => m.set(r.id, releases.length - i))
    return m
  }

  const isAsc = sortOrder === 'asc'

  const entries: { id: number; price: number; quality: number; historicalDiscount: number | null }[] = []
  for (const r of releases) {
    const listing = listingMap.get(r.id)
    const price = listing?.price
    if (price == null || price <= 0) continue

    const quality = sortBy === 'listed'
      ? (listing?.first_seen ? new Date(listing.first_seen).getTime() : 0)
      : getQualityValue(r, sortBy)
    // `median` is Discogs' historical sold-price median.  Unlike the seller's
    // live listing price, it tells us whether a cheap listing is also a good
    // value relative to what copies have actually sold for.
    const historicalDiscount = r.median != null && r.median > 0
      ? Math.max(-1, Math.min(1, (r.median - price) / r.median))
      : null

    entries.push({ id: r.id, price, quality, historicalDiscount })
  }

  if (entries.length === 0) return new Map()

  const prices    = entries.map(e => e.price).sort((a, b) => a - b)
  const qualities = entries.map(e => e.quality).sort((a, b) => a - b)
  const historicalDiscounts = entries
    .map(e => e.historicalDiscount)
    .filter((discount): discount is number => discount !== null)
    .sort((a, b) => a - b)

  const scores = new Map<number, number>()
  for (const e of entries) {
    const priceRank = Math.max(1 - percentileRank(e.price, prices), 0.01)
    const rawQRank  = percentileRank(e.quality, qualities)
    const qualityRank = Math.max(isAsc ? 1 - rawQRank : rawQRank, 0.01)
    const historicalValueRank = e.historicalDiscount === null
      ? priceRank
      : Math.max(percentileRank(e.historicalDiscount, historicalDiscounts), 0.01)

    // A deal needs to be inexpensive *and* discounted versus historical sales.
    // The geometric mean prevents either signal from overwhelming the other;
    // listings without historical data retain the price-only behaviour.
    const valueRank = Math.sqrt(priceRank * historicalValueRank)

    const deal = Math.sqrt(valueRank * qualityRank)
    let base: number
    if (t <= 0.5) {
      const blend = t * 2
      base = (1 - blend) * valueRank + blend * deal
    } else {
      const blend = (t - 0.5) * 2
      base = (1 - blend) * deal + blend * qualityRank
    }

    scores.set(e.id, base)
  }
  return scores
}

function SellerLoadingProgress({ username, progress, onPause }: { username: string; progress: SellerProgress | null; onPause?: () => void }) {
  if (!progress || progress.totalPages === 0) {
    return <LoadingState message={`Connecting to Discogs for ${username}...`} />
  }

  const pct = progress.totalPages > 0
    ? Math.round((progress.page / progress.totalPages) * 100)
    : 0

  const passLabel = progress.totalPasses > 1
    ? `Pass ${progress.pass}/${progress.totalPasses} · `
    : ''

  return (
    <div style={{ padding: '48px 24px', textAlign: 'center' }}>
      <p style={{ marginBottom: '12px', color: 'var(--stonks-text-muted)', fontSize: '0.875rem' }}>
        {passLabel}Fetching page {progress.page} of {progress.totalPages} · {progress.idsFound.toLocaleString()} releases found
      </p>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        margin: '0 auto',
        height: '6px',
        background: 'var(--stonks-border)',
        borderRadius: '3px',
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          background: 'var(--stonks-accent-primary)',
          borderRadius: '3px',
          transition: 'width 0.5s ease',
        }} />
      </div>
      <p style={{ marginTop: '8px', fontSize: '0.75rem', color: 'var(--stonks-text-muted)' }}>
        {pct}%
      </p>
      {onPause && (
        <Button variant="secondary" size="sm" icon="bi bi-pause-fill" onClick={onPause} style={{ marginTop: '12px' }}>
          Pause
        </Button>
      )}
    </div>
  )
}

type SellerContentTab =
  | { type: 'artist'; id: number; artistId: number; name: string }
  | { type: 'label'; id: number; labelName: string; name: string }

export function SellerPage() {
  const { username } = useParams<{ username?: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { viewMode, sort: globalSort, order: globalOrder } = useViewSort()

  const { isAuthenticated } = useDiscogsConnection()
  const { ids: wantlistIds } = useWantlist()
  const wantlist = useMemo(() => new Set(wantlistIds), [wantlistIds])

  // --- All Releases tab state ---
  const [showFilters, setShowFilters] = useState(false)
  const [search, setSearch] = useState('')
  const [dealSlider, setDealSlider] = useState<number | null>(null)
  const [wantlistOnly, setWantlistOnly] = useState(false)
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null)

  const [filters, setFilters] = useState<ReleaseFiltersType>(() => ({
    page: 1,
    per_page: 50,
    sort: globalSort,
    order: globalOrder,
  }))

  // --- Tab system state ---
  const [activeSection, setActiveSection] = useState<'releases' | 'artists' | 'labels' | string>('releases')
  const [openTabs, setOpenTabs] = useState<SellerContentTab[]>([])
  const [subReleasePage, setSubReleasePage] = useState(1)

  // --- Artist browse state ---
  const [artistSearch, setArtistSearch] = useState('')
  const [artistFilters, setArtistFilters] = useState<ArtistFilters>({
    page: 1,
    per_page: 50,
    sort: 'release_count' as ArtistSortField,
    order: 'desc',
  })

  // --- Label browse state ---
  const [labelSearch, setLabelSearch] = useState('')
  const [labelFilters, setLabelFilters] = useState<LabelFilters>({
    page: 1,
    per_page: 50,
    sort: 'release_count' as LabelSortField,
    order: 'desc',
  })

  // Sync global sort/order
  useEffect(() => {
    setFilters(prev => ({ ...prev, sort: globalSort, order: globalOrder }))
  }, [globalSort, globalOrder])

  // Reset on username change
  useEffect(() => {
    setFilters(prev => ({ ...prev, page: 1 }))
    setSearch('')
    setActiveSection('releases')
    setOpenTabs([])
    setSubReleasePage(1)
    setSelectedFolderId(null)
  }, [username])

  // Check if this seller is saved in our DB
  const { data: savedSeller, isLoading: isLoadingSavedSeller } = useQuery({
    queryKey: ['saved-seller', username],
    queryFn: () => getSavedSeller(username!),
    enabled: !!username,
    staleTime: 30 * 1000,
    refetchInterval: (query) => {
      const d = query.state.data
      return (d?.enrichment?.running || d?.status === 'pending') ? 3000 : false
    },
  })

  const isSavedAndReady = savedSeller?.status === 'ready'
  const isSavedAndPending = savedSeller?.status === 'pending'
  const isSavedAndPaused = savedSeller?.status === 'paused'

  const {
    data: sellerData,
    isLoading: isLoadingFromDiscogs,
    error: idsError,
  } = useQuery({
    queryKey: ['seller-all-ids', username],
    queryFn: () => getSellerAllIds(username!),
    enabled: !!username && !isLoadingSavedSeller && !isSavedAndReady && !isSavedAndPending,
    staleTime: 10 * 60 * 1000,
  })

  const isFetching = isLoadingFromDiscogs || isSavedAndPending
  const { data: progress } = useQuery<SellerProgress | null>({
    queryKey: ['seller-progress', username],
    queryFn: () => getSellerProgress(username!),
    enabled: !!username && isFetching,
    refetchInterval: 1000,
    staleTime: 0,
  })

  const releaseIds: number[] = isSavedAndReady
    ? (savedSeller!.release_ids as number[])
    : (sellerData?.releaseIds ?? [])

  const totalListings = isSavedAndReady
    ? savedSeller!.total_listings
    : (sellerData?.totalListings ?? 0)

  const { data: foldersData } = useQuery({
    queryKey: ['folders'],
    queryFn: getFolders,
    staleTime: 60 * 1000,
  })
  const folders = foldersData?.folders ?? []

  const { data: folderFilterData, isFetching: isFetchingFolderFilter } = useQuery({
    queryKey: ['folder-master-filter', selectedFolderId, releaseIds.length],
    queryFn: () => getFolderMasterFilter(selectedFolderId!, releaseIds),
    enabled: selectedFolderId !== null && releaseIds.length > 0,
    staleTime: 5 * 60 * 1000,
  })
  const folderFilteredIds = useMemo(
    () => selectedFolderId !== null ? new Set(folderFilterData?.release_ids ?? []) : null,
    [selectedFolderId, folderFilterData],
  )

  const filteredReleaseIds = useMemo(() => {
    let ids = releaseIds
    if (wantlistOnly) ids = ids.filter(id => wantlist.has(id))
    if (folderFilteredIds !== null) ids = ids.filter(id => folderFilteredIds.has(id))
    return ids
  }, [releaseIds, wantlistOnly, wantlist, folderFilteredIds])

  const queryFilters = useMemo<ReleaseFiltersType>(
    () => ({ ...filters, release_ids: filteredReleaseIds, seller: username }),
    [filters, filteredReleaseIds, username],
  )

  // Normal paginated query
  const { data, isLoading: isLoadingReleases, error: releasesError } = useReleases(queryFilters, {
    enabled: filteredReleaseIds.length > 0 && dealSlider === null && activeSection === 'releases',
  })

  // Deal mode: fetch ALL releases at once
  const dealQueryFilters = useMemo<ReleaseFiltersType>(
    () => ({ ...filters, page: 1, per_page: filteredReleaseIds.length || 1, release_ids: filteredReleaseIds, seller: username }),
    [filters, filteredReleaseIds, username],
  )
  const { data: dealData, isLoading: isLoadingDeal } = useReleases(dealQueryFilters, {
    enabled: filteredReleaseIds.length > 0 && dealSlider !== null && activeSection === 'releases',
    staleTime: 5 * 60 * 1000,
  })

  const allDealReleases = dealData?.releases ?? []

  const releases = dealSlider === null ? (data?.releases ?? []) : []
  const pagination = dealSlider === null ? data?.pagination : undefined

  const pageReleaseIds = useMemo(
    () => dealSlider === null ? releases.map(r => r.id) : [],
    [releases, dealSlider],
  )
  const { data: listingData = [] } = useQuery({
    queryKey: ['seller-listings', username, dealSlider !== null ? '__all__' : pageReleaseIds],
    queryFn: () => getSellerListings(username!, pageReleaseIds),
    enabled: !!username && activeSection === 'releases' && (dealSlider !== null ? filteredReleaseIds.length > 0 : pageReleaseIds.length > 0),
    staleTime: 5 * 60 * 1000,
  })
  const listingMap = useMemo(() => {
    const map = new Map<number, SellerListingInfo>()
    listingData.forEach(l => map.set(l.release_id, l))
    return map
  }, [listingData])

  const activeSortBy = filters.sort || 'elevated_score'
  const activeSortOrder = filters.order || 'desc'
  const dealScoreMap = useMemo(() => {
    if (dealSlider === null) return new Map<number, number>()
    return computeDealScores(allDealReleases, listingMap, dealSlider / 100, activeSortBy, activeSortOrder)
  }, [dealSlider, allDealReleases, listingMap, activeSortBy, activeSortOrder])

  const dealPageSize = filters.per_page ?? 50
  const dealPage = filters.page ?? 1

  const sortedReleases = useMemo(() => {
    if (dealSlider === null) return releases
    if (dealScoreMap.size === 0) return allDealReleases.slice(0, dealPageSize)
    const sorted = [...allDealReleases].sort((a, b) => {
      const sa = dealScoreMap.get(a.id) ?? -Infinity
      const sb = dealScoreMap.get(b.id) ?? -Infinity
      return sb - sa
    })
    const start = (dealPage - 1) * dealPageSize
    return sorted.slice(start, start + dealPageSize)
  }, [dealSlider, dealScoreMap, allDealReleases, releases, dealPage, dealPageSize])

  const dealPagination = dealSlider !== null ? {
    page: dealPage,
    total_pages: Math.ceil(allDealReleases.length / dealPageSize),
    items: allDealReleases.length,
    per_page: dealPageSize,
  } : undefined

  const activePagination = dealSlider !== null ? dealPagination : pagination

  // --- Artist browse query ---
  const artistQueryFilters = useMemo<ArtistFilters>(() => ({
    ...artistFilters,
    q: artistSearch || undefined,
    sort: mapSortForEntity(globalSort) as ArtistSortField || 'release_count',
    order: globalOrder,
    release_ids: filteredReleaseIds,
  }), [artistFilters, artistSearch, globalSort, globalOrder, filteredReleaseIds])

  const { data: artistsData, isLoading: isLoadingArtists } = useArtists(artistQueryFilters, {
    enabled: activeSection === 'artists' && filteredReleaseIds.length > 0,
  })

  // --- Label browse query ---
  const labelQueryFilters = useMemo<LabelFilters>(() => ({
    ...labelFilters,
    q: labelSearch || undefined,
    sort: mapSortForEntity(globalSort) as LabelSortField || 'release_count',
    order: globalOrder,
    release_ids: filteredReleaseIds,
  }), [labelFilters, labelSearch, globalSort, globalOrder, filteredReleaseIds])

  const { data: labelsData, isLoading: isLoadingLabels } = useLabels(labelQueryFilters, {
    enabled: activeSection === 'labels' && filteredReleaseIds.length > 0,
  })

  // --- Active content tab (artist/label) ---
  const activeContentTab = openTabs.find(t => t.id.toString() === activeSection)

  const subReleaseFilters = useMemo<ReleaseFiltersType>(() => ({
    page: subReleasePage,
    per_page: 50,
    sort: globalSort,
    order: globalOrder,
    release_ids: filteredReleaseIds,
    ...(activeContentTab?.type === 'artist' ? { artist_id: activeContentTab.artistId } : {}),
    ...(activeContentTab?.type === 'label' ? { label: activeContentTab.labelName } : {}),
  }), [activeContentTab, subReleasePage, globalSort, globalOrder, filteredReleaseIds])

  const { data: subReleasesData, isLoading: isLoadingSubReleases } = useReleases(subReleaseFilters, {
    enabled: !!activeContentTab && filteredReleaseIds.length > 0,
  })

  // --- Tab handlers ---
  const handleArtistTabOpen = useCallback((artistId: number, artistName: string) => {
    setOpenTabs(prev => {
      const existing = prev.find(t => t.type === 'artist' && t.artistId === artistId)
      if (existing) {
        setActiveSection(existing.id.toString())
        return prev
      }
      const newTab: SellerContentTab = { type: 'artist', id: Date.now(), artistId, name: artistName }
      setActiveSection(newTab.id.toString())
      return [...prev, newTab]
    })
    setSubReleasePage(1)
  }, [])

  const handleLabelTabOpen = useCallback((labelName: string) => {
    setOpenTabs(prev => {
      const existing = prev.find(t => t.type === 'label' && t.labelName === labelName)
      if (existing) {
        setActiveSection(existing.id.toString())
        return prev
      }
      const newTab: SellerContentTab = { type: 'label', id: Date.now(), labelName, name: labelName }
      setActiveSection(newTab.id.toString())
      return [...prev, newTab]
    })
    setSubReleasePage(1)
  }, [])

  const handleCloseTab = useCallback((e: React.MouseEvent, tabId: number) => {
    e.stopPropagation()
    setOpenTabs(prev => prev.filter(t => t.id !== tabId))
    if (activeSection === tabId.toString()) {
      setActiveSection('releases')
      setSubReleasePage(1)
    }
  }, [activeSection])

  const sellerTabContextValue = useMemo(() => ({
    openArtistTab: handleArtistTabOpen,
    openLabelTab: handleLabelTabOpen,
  }), [handleArtistTabOpen, handleLabelTabOpen])

  // --- Save / Refresh mutations ---
  const saveMutation = useMutation({
    mutationFn: () => addSavedSeller(username!),
    onSuccess: () => {
      track('seller_scan', { username })
      queryClient.invalidateQueries({ queryKey: ['saved-sellers'] })
      queryClient.invalidateQueries({ queryKey: ['saved-seller', username] })
      showToast('Seller saved — fetching listings in background', 'success')
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  })

  const refreshMutation = useMutation({
    mutationFn: () => refreshSavedSeller(username!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-sellers'] })
      queryClient.invalidateQueries({ queryKey: ['saved-seller', username] })
      showToast('Re-fetching listings in background', 'info')
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  })

  const pauseFetchMutation = useMutation({
    mutationFn: () => pauseSeller(username!, 'fetch'),
    onSuccess: () => showToast('Fetch pausing after current batch…', 'info'),
  })

  const resumeFetchMutation = useMutation({
    mutationFn: () => resumeSeller(username!, 'fetch'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-seller', username] })
      showToast('Fetch resumed', 'info')
    },
  })

  const pauseEnrichMutation = useMutation({
    mutationFn: () => pauseSeller(username!, 'enrich'),
    onSuccess: () => showToast('Enrichment pausing after current release…', 'info'),
  })

  const resumeEnrichMutation = useMutation({
    mutationFn: () => resumeSeller(username!, 'enrich'),
    onSuccess: () => showToast('Enrichment resumed', 'info'),
  })

  const handleSearch = useCallback((value: string) => {
    setSearch(value)
    setFilters(prev => ({ ...prev, q: value || undefined, page: 1 }))
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => {
        const next = search || undefined
        if (prev.q === next) return prev
        return { ...prev, q: next, page: 1 }
      })
    }, 350)
    return () => clearTimeout(timeout)
  }, [search])

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const isLoadingIds = isFetching
  const error = idsError || releasesError

  // switch section helper
  const switchSection = (section: 'releases' | 'artists' | 'labels') => {
    setActiveSection(section)
    setSubReleasePage(1)
  }

  return (
    <SellerTabContext.Provider value={sellerTabContextValue}>
      <div className={styles.page}>
        {/* Header */}
        <div className={styles.pageHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IconButton
              variant="ghost"
              size="sm"
              icon="bi bi-chevron-left"
              aria-label="Back to sellers"
              title="Back to sellers"
              onClick={() => navigate('/sellers')}
            />
            <h1>
              <i className="bi bi-shop"></i>
              {username ? ` ${username}` : ' Seller Lookup'}
            </h1>
            {(isSavedAndReady || sellerData) && (
              <span style={{ fontSize: '0.8125rem', color: 'var(--stonks-text-muted)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {totalListings > 0 && `${totalListings.toLocaleString()} listings · `}
                {releaseIds.length.toLocaleString()} unique releases
                {sellerData?.truncated && (
                  <span
                    title={`Discogs API limits fetching to 50,000 listings. Only ${releaseIds.length.toLocaleString()} of ${totalListings.toLocaleString()} were retrieved.`}
                    style={{
                      background: 'var(--stonks-warning, #f59e0b)',
                      color: '#000',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: '4px',
                      cursor: 'help',
                    }}
                  >
                    partial
                  </span>
                )}
              </span>
            )}
            {/* Save / Refresh button */}
            {username && !isSavedAndPending && (
              isSavedAndReady ? (
                <Button
                  variant="secondary"
                  size="sm"
                  icon="bi bi-arrow-clockwise"
                  onClick={() => refreshMutation.mutate()}
                  disabled={refreshMutation.isPending}
                  title="Re-fetch listings from Discogs"
                >
                  Refresh
                </Button>
              ) : sellerData ? (
                <Button
                  variant="accentSoft"
                  size="sm"
                  icon="bi bi-bookmark-plus"
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                >
                  Save seller
                </Button>
              ) : null
            )}
            {isSavedAndPending && (
              <span style={{ fontSize: '0.8rem', color: 'var(--stonks-text-muted)' }}>
                <i className="bi bi-hourglass-split"></i> Fetching…
              </span>
            )}
            {isSavedAndReady && (
              <span
                style={{ fontSize: '0.75rem', color: 'var(--stonks-accent-green, #22c55e)' }}
                title={savedSeller?.last_fetched_at ? `Last updated: ${new Date(savedSeller.last_fetched_at).toLocaleString()}` : undefined}
              >
                <i className="bi bi-bookmark-check-fill"></i>
              </span>
            )}
            {savedSeller?.enrichment?.running && (
              <span style={{ fontSize: '0.75rem', color: 'var(--stonks-text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite' }}></i>
                Enriching {savedSeller.enrichment.done}/{savedSeller.enrichment.total}
                <IconButton
                  variant="secondary"
                  size="sm"
                  icon="bi bi-pause-fill"
                  aria-label="Pause enrichment"
                  title="Pause enrichment"
                  onClick={() => pauseEnrichMutation.mutate()}
                  disabled={pauseEnrichMutation.isPending}
                />
              </span>
            )}
            {savedSeller?.enrichment?.paused && !savedSeller.enrichment.running && (
              <span style={{ fontSize: '0.75rem', color: 'var(--stonks-text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i className="bi bi-pause-circle"></i>
                Enrichment paused ({savedSeller.enrichment.done}/{savedSeller.enrichment.total})
                <IconButton
                  variant="secondary"
                  size="sm"
                  icon="bi bi-play-fill"
                  aria-label="Resume enrichment"
                  title="Resume enrichment"
                  onClick={() => resumeEnrichMutation.mutate()}
                  disabled={resumeEnrichMutation.isPending}
                />
              </span>
            )}
            {isSavedAndPaused && (
              <span style={{ fontSize: '0.75rem', color: 'var(--stonks-accent-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i className="bi bi-pause-circle"></i>
                Fetch paused ({progress?.idsFound?.toLocaleString() ?? '?'} releases saved)
                <Button
                  variant="secondary"
                  size="sm"
                  icon="bi bi-play-fill"
                  onClick={() => resumeFetchMutation.mutate()}
                  disabled={resumeFetchMutation.isPending}
                  title="Resume fetching"
                >
                  Resume
                </Button>
              </span>
            )}
          </div>
        </div>

        {/* Main content */}
        {!username ? (
          <div className={styles.empty}>
            <i className="bi bi-shop"></i>
            <p>Enter a Discogs seller username to browse their listings</p>
            <p className={styles.emptyHint}>Shows all releases that are also in your local database</p>
          </div>
        ) : isLoadingIds ? (
          <SellerLoadingProgress username={username} progress={progress ?? null} onPause={isSavedAndPending ? () => pauseFetchMutation.mutate() : undefined} />
        ) : error ? (
          <div className={styles.error}>
            <i className="bi bi-exclamation-triangle"></i>
            <p>{error instanceof Error ? error.message : 'Failed to load seller inventory'}</p>
          </div>
        ) : releaseIds.length === 0 ? (
          <div className={styles.empty}>
            <i className="bi bi-box"></i>
            <p>{username} has no active listings on Discogs</p>
          </div>
        ) : (
          <>
            {/* Tab bar */}
            <div className={releaseStyles.tabBar}>
              <button
                className={cn(releaseStyles.tab, activeSection === 'releases' && releaseStyles.tabActive)}
                onClick={() => switchSection('releases')}
              >
                <i className="bi bi-vinyl"></i> Releases
              </button>
              <button
                className={cn(releaseStyles.tab, activeSection === 'artists' && releaseStyles.tabActive)}
                onClick={() => switchSection('artists')}
              >
                <i className="bi bi-person-badge"></i> Artists
              </button>
              <button
                className={cn(releaseStyles.tab, activeSection === 'labels' && releaseStyles.tabActive)}
                onClick={() => switchSection('labels')}
              >
                <i className="bi bi-tag-fill"></i> Labels
              </button>
              {openTabs.map(tab => (
                <button
                  key={tab.id}
                  className={cn(releaseStyles.tab, activeSection === tab.id.toString() && releaseStyles.tabActive)}
                  onClick={() => { setActiveSection(tab.id.toString()); setSubReleasePage(1) }}
                >
                  {tab.type === 'artist'
                    ? <i className="bi bi-person"></i>
                    : <i className="bi bi-tag"></i>
                  }
                  {' '}{tab.name}
                  <span className={releaseStyles.tabClose} onClick={e => handleCloseTab(e, tab.id)}>
                    <i className="bi bi-x"></i>
                  </span>
                </button>
              ))}
            </div>

            {/* ── Releases section ── */}
            {activeSection === 'releases' && (
              <>
                <ReleaseToolbar
                  search={releaseIds.length > 0 ? search : undefined}
                  onSearchChange={releaseIds.length > 0 ? setSearch : undefined}
                  onSearchSubmit={releaseIds.length > 0 ? handleSearch : undefined}
                  searchPlaceholder="Search releases..."
                  showFilters={showFilters}
                  onToggleFilters={() => setShowFilters(!showFilters)}
                  filters={filters}
                  onFiltersChange={update => setFilters(prev => ({ ...prev, ...update }))}
                  trailing={
                    <>
                      {folders.length > 0 && (
                        <Select
                          value={selectedFolderId ?? ''}
                          onChange={e => {
                            setSelectedFolderId((e.target as HTMLSelectElement).value ? Number((e.target as HTMLSelectElement).value) : null)
                            setFilters(prev => ({ ...prev, page: 1 }))
                          }}
                          style={{
                            maxWidth: 160,
                            backgroundColor: selectedFolderId ? 'var(--stonks-accent-primary)' : undefined,
                            color: selectedFolderId ? 'var(--stonks-accent-contrast)' : undefined,
                          }}
                        >
                          <option value=''>
                            {isFetchingFolderFilter ? 'Filtering…' : 'Filter by folder'}
                          </option>
                          {folders.map(f => (
                            <option key={f.id} value={f.id}>{f.name}</option>
                          ))}
                        </Select>
                      )}
                      {isAuthenticated && (
                        <Button
                          variant={wantlistOnly ? 'primary' : 'secondary'}
                          icon="bi bi-heart-fill"
                          onClick={() => { setWantlistOnly(prev => !prev); setFilters(prev => ({ ...prev, page: 1 })) }}
                          title="Show only releases in your Discogs wantlist"
                        >
                          Wantlist
                        </Button>
                      )}
                      {(isSavedAndReady || listingData.length > 0) && (
                        <>
                          <Button
                            variant={dealSlider !== null ? 'primary' : 'secondary'}
                            icon="bi bi-stars"
                            onClick={() => setDealSlider(prev => prev === null ? 50 : null)}
                            title="Toggle deal mode: blend quality with listing price and historical sales to find the best deals"
                          >
                            Deal Mode
                          </Button>
                          {dealSlider !== null && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: 'var(--stonks-text-muted)' }}>
                              <span>Value</span>
                              <input
                                type="range"
                                min={0}
                                max={100}
                                value={dealSlider}
                                onChange={e => setDealSlider(Number(e.target.value))}
                                style={{ width: 90, accentColor: 'var(--stonks-accent)' }}
                              />
                              <span>Quality</span>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  }
                />

                {showFilters && <ReleaseFilters filters={filters} onFiltersChange={setFilters} onClose={() => setShowFilters(false)} contextReleaseIds={filteredReleaseIds} />}

                {/* Release content */}
                {wantlistOnly && filteredReleaseIds.length === 0 ? (
                  <div className={styles.empty}>
                    <i className="bi bi-heart"></i>
                    <p>None of {username}'s releases are in your wantlist</p>
                    <p className={styles.emptyHint}>{releaseIds.length.toLocaleString()} releases checked</p>
                  </div>
                ) : (isLoadingReleases || isLoadingDeal) ? (
                  <LoadingState message="Matching releases in database..." />
                ) : (
                  <div className={releaseStyles.releasesLayout}>
                    <div className={releaseStyles.tableContainer}>
                      {sortedReleases.length === 0 ? (
                        <div className={styles.empty}>
                          <i className="bi bi-database"></i>
                          <p>None of {username}'s listings are in your database</p>
                          <p className={styles.emptyHint}>
                            {totalListings > 0 && `${totalListings.toLocaleString()} listings checked · `}try adjusting filters
                          </p>
                        </div>
                      ) : viewMode === 'crate' ? (
                        sortedReleases.length > 0 ? (
                          <ReleaseCrateView releases={sortedReleases} />
                        ) : (
                          <div className={styles.empty}><i className="bi bi-search"></i><p>No matching releases</p></div>
                        )
                      ) : (
                        sortedReleases.length > 0 ? (
                          <ReleaseGrid
                            releases={sortedReleases}
                            draggable={true}
                            listingMap={listingMap}
                          />
                        ) : (
                          <div className={styles.empty}><i className="bi bi-search"></i><p>No matching releases</p></div>
                        )
                      )}

                      {activePagination && (
                        <Pagination
                          page={activePagination.page}
                          totalPages={activePagination.total_pages}
                          hasNext={activePagination.page < activePagination.total_pages}
                          hasPrev={activePagination.page > 1}
                          onPageChange={handlePageChange}
                          perPage={filters.per_page}
                          onPerPageChange={per_page => setFilters(prev => ({ ...prev, per_page, page: 1 }))}
                        />
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── Artists section ── */}
            {activeSection === 'artists' && (
              <>
                <div className={artistStyles.controlsRow} style={{ padding: '8px var(--page-padding-x)' }}>
                  <div className={artistStyles.searchBarWrap}>
                    <SearchField
                      value={artistSearch}
                      onChange={setArtistSearch}
                      placeholder="Search artists..."
                      block
                    />
                  </div>
                  <div className={styles.controlsTrailing}>
                    <ViewSortControls />
                  </div>
                </div>
                <div className={styles.gridView}>
                  {isLoadingArtists ? (
                    <LoadingState message="Loading artists..." />
                  ) : artistsData?.artists && artistsData.artists.length > 0 ? (
                    <>
                      <ArtistGrid
                        artists={artistsData.artists}
                        onArtistClick={artist => handleArtistTabOpen(artist.id, artist.name)}
                      />
                      {artistsData.pagination && (
                        <Pagination
                          page={artistsData.pagination.page}
                          totalPages={artistsData.pagination.pages}
                          hasNext={artistsData.pagination.page < artistsData.pagination.pages}
                          hasPrev={artistsData.pagination.page > 1}
                          onPageChange={p => setArtistFilters(prev => ({ ...prev, page: p }))}
                        />
                      )}
                    </>
                  ) : (
                    <div className={styles.empty}>
                      <i className="bi bi-person-circle"></i>
                      <p>No artists found</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ── Labels section ── */}
            {activeSection === 'labels' && (
              <>
                <div className={labelStyles.controlsRow} style={{ padding: '8px var(--page-padding-x)' }}>
                  <div className={labelStyles.searchBarWrap}>
                    <SearchField
                      value={labelSearch}
                      onChange={setLabelSearch}
                      placeholder="Search labels..."
                      block
                    />
                  </div>
                  <div className={styles.controlsTrailing}>
                    <ViewSortControls />
                  </div>
                </div>
                <div className={styles.gridView}>
                  {isLoadingLabels ? (
                    <LoadingState message="Loading labels..." />
                  ) : labelsData?.labels && labelsData.labels.length > 0 ? (
                    <>
                      <LabelGrid
                        labels={labelsData.labels}
                        onLabelClick={label => handleLabelTabOpen(label.name)}
                      />
                      {labelsData.pagination && (
                        <Pagination
                          page={labelsData.pagination.page}
                          totalPages={labelsData.pagination.pages}
                          hasNext={labelsData.pagination.page < labelsData.pagination.pages}
                          hasPrev={labelsData.pagination.page > 1}
                          onPageChange={p => setLabelFilters(prev => ({ ...prev, page: p }))}
                        />
                      )}
                    </>
                  ) : (
                    <div className={styles.empty}>
                      <i className="bi bi-tag"></i>
                      <p>No labels found</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ── Artist / Label tab (release drilldown) ── */}
            {activeContentTab && (
              <div className={releaseStyles.releasesLayout}>
                <div className={releaseStyles.tableContainer}>
                  {isLoadingSubReleases ? (
                    <LoadingState message="Loading releases..." />
                  ) : (
                    <>
                      <ReleaseListView
                        releases={subReleasesData?.releases ?? []}
                        viewMode={viewMode}
                        emptyMessage={
                          activeContentTab.type === 'artist'
                            ? `None of ${username}'s releases match artist "${activeContentTab.name}" in your database`
                            : `None of ${username}'s releases match label "${activeContentTab.name}" in your database`
                        }
                      />
                      {subReleasesData?.pagination && (
                        <Pagination
                          page={subReleasePage}
                          totalPages={subReleasesData.pagination.total_pages}
                          hasNext={subReleasePage < subReleasesData.pagination.total_pages}
                          hasPrev={subReleasePage > 1}
                          onPageChange={p => setSubReleasePage(p)}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </SellerTabContext.Provider>
  )
}
